import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle, Calendar, MessageSquare, FileSearch, Rocket, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { consultationData } from "@/data/mock";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const processIcons = [MessageSquare, FileSearch, Calendar, Rocket];

export default function Consultation() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.service) {
      toast.error("Please select a service", {
        description: "Choose a service you're interested in."
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        service: formData.service,
        message: formData.message || null
      };
      
      await axios.post(`${API}/consultation`, payload);
      
      toast.success("Thank you! We'll be in touch soon.", {
        description: "Your consultation request has been submitted."
      });
      
      setFormData({ name: "", email: "", company: "", service: "", message: "" });
    } catch (error) {
      console.error("Consultation form error:", error);
      const errorMessage = error.response?.data?.detail || "Failed to submit request. Please try again.";
      toast.error("Submission failed", {
        description: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 to-slate-900" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {consultationData.headline}
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed">
              {consultationData.subheadline}
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-2xl font-bold text-white text-center mb-12">Our Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {consultationData.process.map((step, index) => {
              const Icon = processIcons[index];
              return (
                <div key={step.step} className="relative">
                  {index < consultationData.process.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-800" />
                  )}
                  <div className="relative z-10 flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>
                    <div className="text-cyan-400 text-sm font-medium mb-2">Step {step.step}</div>
                    <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-slate-400 text-sm">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">What You'll Get</h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Our consultation process is designed to understand your unique challenges and provide actionable insights.
              </p>
              <div className="space-y-4">
                {consultationData.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                    <span className="text-white">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 rounded-2xl bg-slate-800/50 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-3">Who Is This For?</h3>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li>• Businesses looking to implement AI solutions</li>
                  <li>• Startups building AI-powered products</li>
                  <li>• Teams wanting to upskill in AI technologies</li>
                  <li>• Enterprises seeking digital transformation</li>
                </ul>
              </div>
            </div>

            {/* Form */}
            <div>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Request a Consultation</h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-300">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          required
                          className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-slate-300">Company / Organization</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                        className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Service Interest</Label>
                      <Select value={formData.service} onValueChange={handleServiceChange}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white focus:ring-cyan-500">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="consulting" className="text-white hover:bg-slate-700">AI & Automation Consulting</SelectItem>
                          <SelectItem value="development" className="text-white hover:bg-slate-700">Software & Product Development</SelectItem>
                          <SelectItem value="training" className="text-white hover:bg-slate-700">Corporate Training</SelectItem>
                          <SelectItem value="other" className="text-white hover:bg-slate-700">Other / Not Sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-slate-300">Tell Us About Your Project</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Describe your goals, challenges, or questions..."
                        rows={5}
                        className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold py-6 group"
                    >
                      {isSubmitting ? (
                        "Submitting..."
                      ) : (
                        <>
                          Submit Request
                          <Send className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
